import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../api';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data.data);
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token: newToken, user: newUser } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.info('Logged out successfully');
  };

  // Login with a token (for magic link verification)
  const loginWithToken = async (newToken) => {
    try {
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Fetch user profile with the new token
      const response = await authAPI.getProfile();
      setUser(response.data.data);
      
      return { success: true };
    } catch (error) {
      console.error('Token login failed:', error);
      localStorage.removeItem('token');
      setToken(null);
      return { success: false, message: 'Invalid or expired token' };
    }
  };

  // Login with a single-use token (for returning from external apps)
  const loginWithSingleUseToken = async (singleUseToken) => {
    try {
      // Verify the single-use token with the backend
      const response = await authAPI.verifySingleUseToken(singleUseToken);
      
      if (response.data.success && response.data.user) {
        // If the backend returns a JWT token, use it
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          setToken(response.data.token);
          setUser(response.data.user);
          return { success: true, user: response.data.user };
        }
        
        // Otherwise, just return the user info (external app verified identity)
        return { success: true, user: response.data.user, needsLogin: true };
      }
      
      return { success: false, message: 'Token verification failed' };
    } catch (error) {
      console.error('Single-use token login failed:', error);

      // Handle 429 Too Many Requests (rate limiting from financing-app auto-login)
      if (error.response?.status === 429) {
        const retryAfter = error.response?.headers?.['retry-after'];
        const waitTime = retryAfter ? parseInt(retryAfter) : 60;
        return {
          success: false,
          rateLimited: true,
          retryAfter: waitTime,
          message: `Too many login attempts. Please wait ${waitTime} seconds and try again.`
        };
      }

      return { success: false, message: error.response?.data?.message || 'Invalid or expired token' };
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateProfile(data);
      setUser(response.data.data);
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile';
      toast.error(message);
      return { success: false, message };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithToken,
    loginWithSingleUseToken,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'reviewer',
    isSales: user?.role === 'admin' || user?.role === 'sales',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
